import type { FC } from "react";
import { useRef, useEffect } from "react";

import dayjs from "dayjs";

import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { BrandMentionsSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/BrandMentionsSection";
import { CitationsSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/CitationsSection";
import { CollapsiblePromptsList } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/CollapsiblePromptsList";
import { PromptResponseSection } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/PromptResponseSection";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { PromptCard } from "app/pages/digital-marketing/generative-ai/shared/PromptCard";

import type { IPromptResponse, SentimentTab } from "../types";

interface IPromptsListProps {
    campaign: IAiTrackerCampaign;
    prompts: IPromptResponse[];
    selectedTab: SentimentTab;
}

export const PromptsList: FC<IPromptsListProps> = ({ campaign, prompts, selectedTab }) => {
    const listContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        listContainerRef?.current?.scrollTo({ top: 0 });
    }, [selectedTab, prompts]);

    return (
        <CollapsiblePromptsList
            title={translate("llm.sentiment_report.topics.side-panel.title")}
            listRef={listContainerRef}
        >
            {prompts.map(({ prompt, response, scrapeTime, citations, brands }, index) => {
                const citationsData =
                    citations?.map((citation) => ({
                        icon: citation.icon,
                        source: citation.domain,
                        title: citation.title,
                        description: citation.description,
                        url: citation.link,
                    })) ?? [];

                return (
                    <PromptCard
                        key={index}
                        text={prompt}
                        textFooterElements={
                            scrapeTime
                                ? [
                                      <Text
                                          key="scrape-time"
                                          fontVariant="listItem3"
                                          color={colorsPalettes.carbon[400]}
                                      >
                                          {dayjs(scrapeTime).format(shortMonthDayAndYear)}
                                      </Text>,
                                  ]
                                : null
                        }
                    >
                        <PromptResponseSection
                            campaign={campaign}
                            responseText={response}
                        />
                        <BrandMentionsSection brands={brands} />
                        <CitationsSection
                            citations={citationsData}
                            defaultExpanded={false}
                        />
                    </PromptCard>
                );
            })}
        </CollapsiblePromptsList>
    );
};
